/**
 * Endpoint 2 — GET calculator/app/productid/{id} (research/07 §2; REQ-API-6).
 * Verbatim port of process_product_page.js fetchCalculatorByProduct: the whole
 * call sits in a swallow-all try/catch (`catch {}` → undefined) — a 204's
 * empty-body json() rejection, network failures, everything, is silenced and
 * the caller falls back to the DOM-attribute path.
 *
 * DIV-011 (Crisp ses_1c6b4bd2, Foilman): the pageurl and productid lookups used
 * to run strictly back-to-back — DetectPage awaited the pageurl lookup, and only
 * after its response did ProcessProductPage fire this one. On a product page the
 * pageurl lookup is (all but) always a 204 (non-product mappings are keyed to
 * /pages/, /collections/ and landing-page URLs; a store on a custom domain
 * stores myshopify URLs, so its pageurl lookup can NEVER match), so that was one
 * whole API round-trip of dead wait on every product-page load — ~200-400ms
 * measured, and worse the further the shopper is from our servers.
 *
 * `prefetchCalculatorByProduct()` lets DetectPage kick this request off the
 * moment it has a product id, IN PARALLEL with the pageurl lookup. The two
 * round-trips now overlap. `fetchCalculatorByProduct()` — still the only thing
 * ProcessProductPage calls — hands back that in-flight promise instead of
 * issuing a second identical request. Which calculator loads is unchanged: the
 * pageurl result still takes precedence (DetectPage), so a page that IS a mapped
 * non-product page still renders that calculator and the prefetched product
 * response is simply discarded unused.
 */
import { appState } from "../state/app-state";
import { fetchRetryingOnce } from "./fetch-retry";
import { resolution } from "./calculator-resolve";

let prefetched: Promise<any> | undefined;

async function requestCalculatorByProduct(): Promise<any> {
  try {
    let cpcCalcualtor: any;
    // DIV-022: only a request that never completed is retried. A 204's empty-body
    // json() rejection below is the ordinary "no calculator for this product" answer
    // and is still swallowed untouched — it happens AFTER fetch resolved.
    await fetchRetryingOnce(
      appState.baseCalculatorNewApiUrl +
        "calculator/app/productid/" +
        appState.cpc_productId +
        "?shop=" +
        appState.cpc_shopUrl,
    )
      .then((response) => response.json())
      .then(async function (cpc_calcProduct) {
        if (cpc_calcProduct !== undefined) {
          cpcCalcualtor = cpc_calcProduct;
        }
      });
    return cpcCalcualtor;
  } catch (e) {}
}

/**
 * Fire the product lookup NOW (parallel with the pageurl lookup) so its latency
 * overlaps instead of stacking. Idempotent and a no-op without a product id.
 * Safe to call eagerly: `requestCalculatorByProduct` swallows every error.
 */
export function prefetchCalculatorByProduct(): void {
  if (prefetched === undefined && appState.cpc_productId > 0) {
    prefetched = viaResolveOrRequest();
  }
}

/**
 * DIV-027: the resolve call already answered this — the same `{ calcUniqueIden }`
 * shape the productid endpoint returns, or null when the API knows no assignment
 * (the theme-DOM fallback then runs, as it always did). Only when resolve is
 * unavailable does the original request go out.
 */
async function viaResolveOrRequest(): Promise<any> {
  const resolved = await resolution();
  if (resolved === "unavailable") return requestCalculatorByProduct();
  if (resolved !== "none" && resolved.mode === "product") return { calcUniqueIden: resolved.calculatorId };
  return null;
}

export async function fetchCalculatorByProduct(): Promise<any> {
  if (prefetched !== undefined) {
    const inflight = prefetched;
    prefetched = undefined;
    return inflight;
  }
  return viaResolveOrRequest();
}
